import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from './store';
import { getCsrfToken } from '../utils/helpers';
import { clearSession } from './AuthSlice';
import { showAlert } from './CommonSlice';
import { AlertType } from '../types/common';

export type ReviewStatus = 'pending' | 'published' | 'rejected' | 'deleted';

export type ReviewUpdateEntry = {
    id: string;
    text: string;
    createdAt: string;
};

export type ShopReply = { text: string; createdAt: string };

export type ProductReview = {
    id: string;
    author: string;
    rating: number;
    text: string;
    createdAt: string;
    pros?: string;
    cons?: string;
    isVerifiedPurchase?: boolean;
    updates?: ReviewUpdateEntry[];
    status?: ReviewStatus;
    rejectionReason?: string | null;
    deletedAt?: string | null;
    helpfulCount?: number;
    unhelpfulCount?: number;
    shopReply?: ShopReply | null;
};

type ReviewsByProduct = Record<string, ProductReview[]>;
type MyReviewByProduct = Record<string, ProductReview | null>;
type VotesByReview = Record<string, 'up' | 'down' | null>;

export type ReviewsState = {
    byProduct: ReviewsByProduct;
    myByProduct: MyReviewByProduct;
    myVotes: VotesByReview;
    loading: boolean;
    error: string | null;
};

export type AdminReviewItem = ProductReview & {
    productId: string;
    productName?: string;
};

export type AdminReviewsQuery = {
    status?: ReviewStatus | 'all';
    productId?: string;
    q?: string;
    page?: number;
    limit?: number;
};

type AdminState = {
    items: AdminReviewItem[];
    total: number;
    page: number;
    limit: number;
    loading: boolean;
    error: string | null;
    globalReviewsEnabled: boolean | null;
    globalUpdatedAt: string | null;
};

const initialState: ReviewsState & { admin?: AdminState } = {
    byProduct: {},
    myByProduct: {},
    myVotes: {},
    loading: false,
    error: null,
    admin: {
        items: [],
        total: 0,
        page: 1,
        limit: 20,
        loading: false,
        error: null,
        globalReviewsEnabled: null,
        globalUpdatedAt: null,
    },
};

const BASE_URL = String(process.env.REACT_APP_SERVER_URL || '').trim();

function ensureBaseUrl() {
    if (!BASE_URL) throw new Error('REACT_APP_SERVER_URL не задан');
}

export const fetchPublishedReviews = createAsyncThunk<
    { productId: string; reviews: ProductReview[] },
    { productId: string; sort?: 'helpful' | 'new' },
    { state: RootState }
>('reviews/fetchPublishedReviews', async ({ productId, sort = 'helpful' }, { rejectWithValue }) => {
    try {
        ensureBaseUrl();
        const pid = String(productId || '').trim();
        if (!pid) return rejectWithValue('Некорректный productId');

        const resp = await fetch(`${BASE_URL}/reviews/product/${encodeURIComponent(pid)}?sort=${encodeURIComponent(sort)}`, {
            method: 'GET',
            credentials: 'include',
        });

        if (!resp.ok) {
            const j = await resp.json().catch(() => null);
            const t = j?.message ? String(j.message) : await resp.text().catch(() => '');
            return rejectWithValue(t || 'Ошибка загрузки отзывов');
        }

        const data = await resp.json();
        const arr = Array.isArray(data?.reviews) ? (data.reviews as ProductReview[]) : [];
        return { productId: pid, reviews: arr };
    } catch (e: any) {
        return rejectWithValue(e?.message || 'Ошибка загрузки отзывов');
    }
});

export const fetchMyReview = createAsyncThunk<{ productId: string; review: ProductReview | null }, { productId: string }, { state: RootState }>(
    'reviews/fetchMyReview',
    async ({ productId }, { dispatch, rejectWithValue }) => {
        try {
            ensureBaseUrl();
            const pid = String(productId || '').trim();
            if (!pid) return rejectWithValue('Некорректный productId');

            const resp = await fetch(`${BASE_URL}/reviews/product/${encodeURIComponent(pid)}/my`, {
                method: 'GET',
                credentials: 'include',
            });

            if (resp.status === 401) {
                dispatch(clearSession());
                return rejectWithValue('Нет доступа');
            }

            if (!resp.ok) {
                const j = await resp.json().catch(() => null);
                const t = j?.message ? String(j.message) : await resp.text().catch(() => '');
                return rejectWithValue(t || 'Ошибка загрузки отзыва');
            }

            const data = await resp.json();
            const review = (data?.review as ProductReview | null) ?? null;
            return { productId: pid, review };
        } catch (e: any) {
            return rejectWithValue(e?.message || 'Ошибка загрузки отзыва');
        }
    },
);

export const createReview = createAsyncThunk<
    { productId: string; review: ProductReview },
    { productId: string; payload: Pick<ProductReview, 'author' | 'rating' | 'text' | 'pros' | 'cons'> },
    { state: RootState }
>('reviews/createReview', async ({ productId, payload }, { dispatch, rejectWithValue }) => {
    try {
        ensureBaseUrl();
        const pid = String(productId || '').trim();
        if (!pid) return rejectWithValue('Некорректный productId');

        const csrfToken = await getCsrfToken();
        const resp = await fetch(`${BASE_URL}/reviews/product/${encodeURIComponent(pid)}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'csrf-token': csrfToken,
            },
            credentials: 'include',
            body: JSON.stringify(payload),
        });

        if (resp.status === 401) {
            dispatch(clearSession());
            return rejectWithValue('Нет доступа');
        }

        if (!resp.ok) {
            const j = await resp.json().catch(() => null);
            const code = j?.code ? String(j.code) : '';
            const msg = j?.message || 'Ошибка создания отзыва';
            dispatch(showAlert({ type: AlertType.Error, message: msg }));
            return rejectWithValue(code ? `${code}:${msg}` : msg);
        }

        const data = await resp.json();
        const review = data?.review as ProductReview;

        dispatch(showAlert({ type: AlertType.Success, message: 'Отзыв отправлен на модерацию' }));
        return { productId: pid, review };
    } catch (e: any) {
        const msg = e?.message || 'Ошибка создания отзыва';
        dispatch(showAlert({ type: AlertType.Error, message: msg }));
        return rejectWithValue(msg);
    }
});

export const updateReview = createAsyncThunk<
    { productId: string; review: ProductReview },
    { reviewId: string; productId: string; payload: Pick<ProductReview, 'author' | 'rating' | 'text' | 'pros' | 'cons'> },
    { state: RootState }
>('reviews/updateReview', async ({ reviewId, productId, payload }, { dispatch, rejectWithValue }) => {
    try {
        ensureBaseUrl();
        const rid = String(reviewId || '').trim();
        const pid = String(productId || '').trim();
        if (!rid) return rejectWithValue('Некорректный reviewId');
        if (!pid) return rejectWithValue('Некорректный productId');

        const csrfToken = await getCsrfToken();
        const resp = await fetch(`${BASE_URL}/reviews/${encodeURIComponent(rid)}`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'csrf-token': csrfToken,
            },
            credentials: 'include',
            body: JSON.stringify(payload),
        });

        if (resp.status === 401) {
            dispatch(clearSession());
            return rejectWithValue('Нет доступа');
        }

        if (!resp.ok) {
            const j = await resp.json().catch(() => null);
            const msg = j?.message || 'Ошибка редактирования отзыва';
            dispatch(showAlert({ type: AlertType.Error, message: msg }));
            return rejectWithValue(msg);
        }

        const data = await resp.json();
        const review = data?.review as ProductReview;

        dispatch(showAlert({ type: AlertType.Success, message: 'Отзыв обновлён и отправлен на модерацию' }));
        return { productId: pid, review };
    } catch (e: any) {
        const msg = e?.message || 'Ошибка редактирования отзыва';
        dispatch(showAlert({ type: AlertType.Error, message: msg }));
        return rejectWithValue(msg);
    }
});

export const deleteReview = createAsyncThunk<{ productId: string; reviewId: string }, { productId: string; reviewId: string }, { state: RootState }>(
    'reviews/deleteReview',
    async ({ productId, reviewId }, { dispatch, rejectWithValue }) => {
        try {
            ensureBaseUrl();
            const pid = String(productId || '').trim();
            const rid = String(reviewId || '').trim();
            if (!pid) return rejectWithValue('Некорректный productId');
            if (!rid) return rejectWithValue('Некорректный reviewId');

            const csrfToken = await getCsrfToken();
            const resp = await fetch(`${BASE_URL}/reviews/${encodeURIComponent(rid)}`, {
                method: 'DELETE',
                headers: {
                    'csrf-token': csrfToken,
                },
                credentials: 'include',
            });

            if (resp.status === 401) {
                dispatch(clearSession());
                return rejectWithValue('Нет доступа');
            }

            if (!resp.ok) {
                const j = await resp.json().catch(() => null);
                const msg = j?.message || 'Ошибка удаления отзыва';
                dispatch(showAlert({ type: AlertType.Error, message: msg }));
                return rejectWithValue(msg);
            }

            dispatch(showAlert({ type: AlertType.Info, message: 'Отзыв удалён' }));
            return { productId: pid, reviewId: rid };
        } catch (e: any) {
            const msg = e?.message || 'Ошибка удаления отзыва';
            dispatch(showAlert({ type: AlertType.Error, message: msg }));
            return rejectWithValue(msg);
        }
    },
);

export const restoreReview = createAsyncThunk<{ productId: string; reviewId: string }, { productId: string; reviewId: string }, { state: RootState }>(
    'reviews/restoreReview',
    async ({ productId, reviewId }, { dispatch, rejectWithValue }) => {
        try {
            ensureBaseUrl();
            const pid = String(productId || '').trim();
            const rid = String(reviewId || '').trim();
            if (!pid) return rejectWithValue('Некорректный productId');
            if (!rid) return rejectWithValue('Некорректный reviewId');

            const csrfToken = await getCsrfToken();
            const resp = await fetch(`${BASE_URL}/reviews/${encodeURIComponent(rid)}/restore`, {
                method: 'POST',
                headers: {
                    'csrf-token': csrfToken,
                },
                credentials: 'include',
            });

            if (resp.status === 401) {
                dispatch(clearSession());
                return rejectWithValue('Нет доступа');
            }

            if (!resp.ok) {
                const j = await resp.json().catch(() => null);
                const msg = j?.message || 'Ошибка восстановления отзыва';
                dispatch(showAlert({ type: AlertType.Error, message: msg }));
                return rejectWithValue(msg);
            }

            dispatch(showAlert({ type: AlertType.Success, message: 'Отзыв восстановлен и отправлен на модерацию' }));
            return { productId: pid, reviewId: rid };
        } catch (e: any) {
            const msg = e?.message || 'Ошибка восстановления отзыва';
            dispatch(showAlert({ type: AlertType.Error, message: msg }));
            return rejectWithValue(msg);
        }
    },
);

export const voteReview = createAsyncThunk<
    { reviewId: string; vote: 'up' | 'down' | null; helpfulCount: number; unhelpfulCount: number },
    { reviewId: string; vote: 'up' | 'down' },
    { state: RootState }
>('reviews/voteReview', async ({ reviewId, vote }, { dispatch, rejectWithValue }) => {
    try {
        ensureBaseUrl();
        const rid = String(reviewId || '').trim();
        if (!rid) return rejectWithValue('Некорректный reviewId');

        const csrfToken = await getCsrfToken();
        const resp = await fetch(`${BASE_URL}/reviews/${encodeURIComponent(rid)}/vote`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'csrf-token': csrfToken,
            },
            credentials: 'include',
            body: JSON.stringify({ vote }),
        });

        if (resp.status === 401) {
            dispatch(clearSession());
            return rejectWithValue('Нет доступа');
        }

        if (!resp.ok) {
            const j = await resp.json().catch(() => null);
            const msg = j?.message || 'Ошибка голосования';
            dispatch(showAlert({ type: AlertType.Error, message: msg }));
            return rejectWithValue(msg);
        }

        const data = await resp.json();
        return {
            reviewId: rid,
            vote: data?.vote === 'up' || data?.vote === 'down' ? data.vote : null,
            helpfulCount: Number.isFinite(Number(data?.helpfulCount)) ? Number(data.helpfulCount) : 0,
            unhelpfulCount: Number.isFinite(Number(data?.unhelpfulCount)) ? Number(data.unhelpfulCount) : 0,
        };
    } catch (e: any) {
        dispatch(showAlert({ type: AlertType.Error, message: e?.message || 'Ошибка голосования' }));
        return rejectWithValue(e?.message || 'Ошибка голосования');
    }
});

export const addReviewUpdate = createAsyncThunk<
    { reviewId: string; productId: string; updates: ReviewUpdateEntry[] },
    { reviewId: string; productId: string; text: string },
    { state: RootState }
>('reviews/addReviewUpdate', async ({ reviewId, productId, text }, { dispatch, rejectWithValue }) => {
    try {
        ensureBaseUrl();
        const rid = String(reviewId || '').trim();
        const pid = String(productId || '').trim();
        if (!rid) return rejectWithValue('Некорректный reviewId');
        if (!pid) return rejectWithValue('Некорректный productId');

        const csrfToken = await getCsrfToken();
        const resp = await fetch(`${BASE_URL}/reviews/${encodeURIComponent(rid)}/update`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'csrf-token': csrfToken },
            credentials: 'include',
            body: JSON.stringify({ text }),
        });

        if (resp.status === 401) {
            dispatch(clearSession());
            return rejectWithValue('Нет доступа');
        }

        if (!resp.ok) {
            const j = await resp.json().catch(() => null);
            const msg = j?.message || 'Ошибка добавления дополнения';
            dispatch(showAlert({ type: AlertType.Error, message: msg }));
            return rejectWithValue(msg);
        }

        const data = await resp.json();
        const updates = Array.isArray(data?.updates) ? (data.updates as ReviewUpdateEntry[]) : [];
        dispatch(showAlert({ type: AlertType.Success, message: 'Дополнение добавлено' }));
        return { reviewId: rid, productId: pid, updates };
    } catch (e: any) {
        const msg = e?.message || 'Ошибка добавления дополнения';
        dispatch(showAlert({ type: AlertType.Error, message: msg }));
        return rejectWithValue(msg);
    }
});

export const adminFetchReviews = createAsyncThunk<{ items: AdminReviewItem[]; total: number; page: number; limit: number }, AdminReviewsQuery, { state: RootState }>(
    'reviews/adminFetchReviews',
    async (q, { dispatch, rejectWithValue }) => {
        try {
            ensureBaseUrl();
            const params = new URLSearchParams();
            if (q.status) params.set('status', String(q.status));
            if (q.productId) params.set('productId', String(q.productId));
            if (q.q) params.set('q', String(q.q));
            params.set('page', String(q.page || 1));
            params.set('limit', String(q.limit || 20));

            const resp = await fetch(`${BASE_URL}/reviews/admin/list?${params.toString()}`, { method: 'GET', credentials: 'include' });

            if (resp.status === 401) {
                dispatch(clearSession());
                return rejectWithValue('Нет доступа');
            }
            if (!resp.ok) {
                const j = await resp.json().catch(() => null);
                return rejectWithValue(j?.message || 'Ошибка загрузки (admin)');
            }

            const data = await resp.json();
            return {
                items: Array.isArray(data?.items) ? (data.items as AdminReviewItem[]) : [],
                total: Number.isFinite(Number(data?.total)) ? Number(data.total) : 0,
                page: Number.isFinite(Number(data?.page)) ? Number(data.page) : 1,
                limit: Number.isFinite(Number(data?.limit)) ? Number(data.limit) : q.limit || 20,
            };
        } catch (e: any) {
            return rejectWithValue(e?.message || 'Ошибка загрузки (admin)');
        }
    },
);

export const adminSetReviewPublished = createAsyncThunk<{ reviewId: string; status: ReviewStatus }, { reviewId: string; published: boolean }, { state: RootState }>(
    'reviews/adminSetReviewPublished',
    async ({ reviewId, published }, { dispatch, rejectWithValue }) => {
        try {
            ensureBaseUrl();
            const csrfToken = await getCsrfToken();

            const resp = await fetch(`${BASE_URL}/reviews/admin/${encodeURIComponent(reviewId)}/publish`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'csrf-token': csrfToken },
                credentials: 'include',
                body: JSON.stringify({ published }),
            });

            if (resp.status === 401) {
                dispatch(clearSession());
                return rejectWithValue('Нет доступа');
            }
            if (!resp.ok) {
                const j = await resp.json().catch(() => null);
                const msg = j?.message || 'Ошибка изменения публикации';
                dispatch(showAlert({ type: AlertType.Error, message: msg }));
                return rejectWithValue(msg);
            }

            const data = await resp.json();
            const st = (data?.status as ReviewStatus) || (published ? 'published' : 'pending');
            dispatch(showAlert({ type: AlertType.Success, message: published ? 'Отзыв опубликован' : 'Публикация снята' }));
            return { reviewId, status: st };
        } catch (e: any) {
            const msg = e?.message || 'Ошибка изменения публикации';
            dispatch(showAlert({ type: AlertType.Error, message: msg }));
            return rejectWithValue(msg);
        }
    },
);

export const adminReplyToReview = createAsyncThunk<
    { reviewId: string; shopReply: { text: string; createdAt: string } },
    { reviewId: string; text: string },
    { state: RootState }
>('reviews/adminReplyToReview', async ({ reviewId, text }, { dispatch, rejectWithValue }) => {
    try {
        ensureBaseUrl();
        const csrfToken = await getCsrfToken();

        const resp = await fetch(`${BASE_URL}/reviews/admin/${encodeURIComponent(reviewId)}/reply`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'csrf-token': csrfToken },
            credentials: 'include',
            body: JSON.stringify({ text }),
        });

        if (resp.status === 401) {
            dispatch(clearSession());
            return rejectWithValue('Нет доступа');
        }
        if (!resp.ok) {
            const j = await resp.json().catch(() => null);
            const msg = j?.message || 'Ошибка ответа';
            dispatch(showAlert({ type: AlertType.Error, message: msg }));
            return rejectWithValue(msg);
        }

        const data = await resp.json();
        dispatch(showAlert({ type: AlertType.Success, message: 'Ответ сохранён' }));
        return { reviewId, shopReply: data?.shopReply };
    } catch (e: any) {
        const msg = e?.message || 'Ошибка ответа';
        dispatch(showAlert({ type: AlertType.Error, message: msg }));
        return rejectWithValue(msg);
    }
});

export const adminCreateFakeReview = createAsyncThunk<
    { review: AdminReviewItem },
    { productId: string; payload: { author: string; rating: number; text: string; pros?: string; cons?: string; publishNow: boolean } },
    { state: RootState }
>('reviews/adminCreateFakeReview', async ({ productId, payload }, { dispatch, rejectWithValue }) => {
    try {
        ensureBaseUrl();
        const csrfToken = await getCsrfToken();

        const resp = await fetch(`${BASE_URL}/reviews/admin/fake`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'csrf-token': csrfToken },
            credentials: 'include',
            body: JSON.stringify({ productId, ...payload }),
        });

        if (resp.status === 401) {
            dispatch(clearSession());
            return rejectWithValue('Нет доступа');
        }
        if (!resp.ok) {
            const j = await resp.json().catch(() => null);
            const msg = j?.message || 'Ошибка создания фейкового отзыва';
            dispatch(showAlert({ type: AlertType.Error, message: msg }));
            return rejectWithValue(msg);
        }

        const data = await resp.json();
        dispatch(showAlert({ type: AlertType.Success, message: payload.publishNow ? 'Фейковый отзыв опубликован' : 'Фейковый отзыв создан (на модерации)' }));
        return { review: data?.review as AdminReviewItem };
    } catch (e: any) {
        const msg = e?.message || 'Ошибка создания фейкового отзыва';
        dispatch(showAlert({ type: AlertType.Error, message: msg }));
        return rejectWithValue(msg);
    }
});

export const adminGetGlobalReviewsEnabled = createAsyncThunk<
    { enabled: boolean | null; updatedAt: string | null; totalProducts: number; enabledCount: number; disabledCount: number },
    void,
    { state: RootState }
>('reviews/adminGetGlobalReviewsEnabled', async (_, { dispatch, rejectWithValue }) => {
    try {
        ensureBaseUrl();
        const resp = await fetch(`${BASE_URL}/reviews/admin/reviews-enabled`, { method: 'GET', credentials: 'include' });

        if (resp.status === 401) {
            dispatch(clearSession());
            return rejectWithValue('Нет доступа');
        }
        if (!resp.ok) {
            const j = await resp.json().catch(() => null);
            return rejectWithValue(j?.message || 'Ошибка загрузки глобального статуса');
        }

        const data = await resp.json();
        return {
            enabled: typeof data?.enabled === 'boolean' ? data.enabled : null,
            updatedAt: data?.updatedAt ? String(data.updatedAt) : null,
            totalProducts: Number(data?.totalProducts) || 0,
            enabledCount: Number(data?.enabledCount) || 0,
            disabledCount: Number(data?.disabledCount) || 0,
        };
    } catch (e: any) {
        return rejectWithValue(e?.message || 'Ошибка загрузки глобального статуса');
    }
});

export const adminSetGlobalReviewsEnabled = createAsyncThunk<{ enabled: boolean; modifiedCount: number }, { enabled: boolean }, { state: RootState }>(
    'reviews/adminSetGlobalReviewsEnabled',
    async ({ enabled }, { dispatch, rejectWithValue }) => {
        try {
            ensureBaseUrl();
            const csrfToken = await getCsrfToken();

            const resp = await fetch(`${BASE_URL}/reviews/admin/reviews-enabled-bulk`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'csrf-token': csrfToken },
                credentials: 'include',
                body: JSON.stringify({ enabled }),
            });

            if (resp.status === 401) {
                dispatch(clearSession());
                return rejectWithValue('Нет доступа');
            }
            if (!resp.ok) {
                const j = await resp.json().catch(() => null);
                const msg = j?.message || 'Ошибка массового обновления';
                dispatch(showAlert({ type: AlertType.Error, message: msg }));
                return rejectWithValue(msg);
            }

            const data = await resp.json();
            dispatch(showAlert({ type: AlertType.Success, message: enabled ? 'Отзывы включены для всех товаров' : 'Отзывы отключены для всех товаров' }));
            return { enabled: Boolean(data?.enabled), modifiedCount: Number(data?.modifiedCount) || 0 };
        } catch (e: any) {
            const msg = e?.message || 'Ошибка массового обновления';
            dispatch(showAlert({ type: AlertType.Error, message: msg }));
            return rejectWithValue(msg);
        }
    },
);

const reviewsSlice = createSlice({
    name: 'reviews',
    initialState,
    reducers: {
        patchCountsInState: (state, action: PayloadAction<{ reviewId: string; helpfulCount: number; unhelpfulCount: number }>) => {
            const { reviewId, helpfulCount, unhelpfulCount } = action.payload;
            for (const pid of Object.keys(state.byProduct)) {
                const list = state.byProduct[pid];
                const idx = list.findIndex((r) => r.id === reviewId);
                if (idx >= 0) {
                    list[idx] = { ...list[idx], helpfulCount, unhelpfulCount };
                }
            }
            for (const pid of Object.keys(state.myByProduct)) {
                const mr = state.myByProduct[pid];
                if (mr?.id === reviewId) state.myByProduct[pid] = { ...mr, helpfulCount, unhelpfulCount };
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPublishedReviews.pending, (s) => {
                s.loading = true;
                s.error = null;
            })
            .addCase(fetchPublishedReviews.fulfilled, (s, a) => {
                s.loading = false;
                s.byProduct[a.payload.productId] = Array.isArray(a.payload.reviews) ? a.payload.reviews : [];
            })
            .addCase(fetchPublishedReviews.rejected, (s, a: any) => {
                s.loading = false;
                s.error = String(a.payload || a.error?.message || 'Ошибка');
            });

        builder
            .addCase(fetchMyReview.fulfilled, (s, a) => {
                s.myByProduct[a.payload.productId] = a.payload.review ?? null;
            })
            .addCase(fetchMyReview.rejected, (s, a: any) => {
                s.error = s.error || String(a.payload || a.error?.message || '');
            });

        builder
            .addCase(createReview.fulfilled, (s, a) => {
                s.myByProduct[a.payload.productId] = a.payload.review;
            })
            .addCase(createReview.rejected, (_s, a: any) => {
                // оставим один алерт для UX
                // (не используем showAlert тут, потому что thunk уже делает его на success)
            });

        builder.addCase(updateReview.fulfilled, (s, a) => {
            s.myByProduct[a.payload.productId] = a.payload.review;
        });

        builder.addCase(deleteReview.fulfilled, (s, a) => {
            const mr = s.myByProduct[a.payload.productId];
            if (mr?.id === a.payload.reviewId) {
                s.myByProduct[a.payload.productId] = { ...mr, status: 'deleted', deletedAt: new Date().toISOString() };
            }
        });

        builder.addCase(restoreReview.fulfilled, (s, a) => {
            const mr = s.myByProduct[a.payload.productId];
            if (mr?.id === a.payload.reviewId) {
                s.myByProduct[a.payload.productId] = { ...mr, status: 'pending', deletedAt: null };
            }
        });

        builder.addCase(voteReview.fulfilled, (s, a) => {
            s.myVotes[a.payload.reviewId] = a.payload.vote;
            for (const pid of Object.keys(s.byProduct)) {
                const list = s.byProduct[pid];
                const idx = list.findIndex((r) => r.id === a.payload.reviewId);
                if (idx >= 0) list[idx] = { ...list[idx], helpfulCount: a.payload.helpfulCount, unhelpfulCount: a.payload.unhelpfulCount };
            }
            for (const pid of Object.keys(s.myByProduct)) {
                const mr = s.myByProduct[pid];
                if (mr?.id === a.payload.reviewId) s.myByProduct[pid] = { ...mr, helpfulCount: a.payload.helpfulCount, unhelpfulCount: a.payload.unhelpfulCount };
            }
        });

        builder.addCase(addReviewUpdate.fulfilled, (s, a) => {
            const pid = a.payload.productId;
            const mr = s.myByProduct[pid];
            if (mr && mr.id === a.payload.reviewId) {
                s.myByProduct[pid] = { ...mr, updates: a.payload.updates };
            }
        });

        builder.addCase(voteReview.rejected, (_s, a: any) => {
            // покажем один алерт об ошибке
            // NOTE: showAlert нельзя диспатчить из reducer, поэтому остаётся на компонент/внешний слой
        });

        builder
            .addCase(adminFetchReviews.pending, (s) => {
                if (!s.admin) return;
                s.admin.loading = true;
                s.admin.error = null;
            })
            .addCase(adminFetchReviews.fulfilled, (s, a) => {
                if (!s.admin) return;
                s.admin.loading = false;
                s.admin.items = a.payload.items || [];
                s.admin.total = a.payload.total || 0;
                s.admin.page = a.payload.page || 1;
                s.admin.limit = a.payload.limit || 20;
            })
            .addCase(adminFetchReviews.rejected, (s, a: any) => {
                if (!s.admin) return;
                s.admin.loading = false;
                s.admin.error = String(a.payload || a.error?.message || 'Ошибка');
            });

        builder.addCase(adminGetGlobalReviewsEnabled.fulfilled, (s, a) => {
            if (!s.admin) return;
            s.admin.globalReviewsEnabled = a.payload.enabled;
            s.admin.globalUpdatedAt = a.payload.updatedAt;
        });

        builder.addCase(adminSetReviewPublished.fulfilled, (s, a) => {
            const it = s.admin?.items?.find((x) => x.id === a.payload.reviewId);
            if (it) it.status = a.payload.status;
        });

        builder.addCase(adminReplyToReview.fulfilled, (s, a) => {
            const it = s.admin?.items?.find((x) => x.id === a.payload.reviewId);
            if (it) it.shopReply = a.payload.shopReply;
        });
    },
});

export const { patchCountsInState } = reviewsSlice.actions;

export const selectPublishedReviews = (state: RootState, productId: string) => state.reviews.byProduct[String(productId || '').trim()] || [];
export const selectMyReview = (state: RootState, productId: string) => state.reviews.myByProduct[String(productId || '').trim()] ?? null;
export const selectMyVote = (state: RootState, reviewId: string) => state.reviews.myVotes[String(reviewId || '').trim()] ?? null;

export default reviewsSlice.reducer;
